import React, {useCallback, useEffect, useRef} from 'react'
import styled from '@emotion/styled'

import {Box} from '@vio/atlas/helpers/Box'
import {Button} from '@vio/atlas/src/components/designSystem/Button'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {BetterValueCarouselSkeleton} from './BetterValueCarouselSkeleton'
import {BetterValueHotelCard} from './BetterValueHotelCard'
import {BetterValueCarouselProps} from './types'

const CarouselWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s400};
`

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  margin-right: calc(-1 * ${cssTheme.layout.spacing.s500});
  padding-right: ${cssTheme.layout.spacing.s500};
  gap: ${cssTheme.layout.spacing.s400};

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const SlideWrapper = styled.div`
  min-width: 156px;
  max-width: 156px;
  scroll-snap-align: start;
`

export const BetterValueCarousel = ({
  hotels,
  locationName,
  isLoading,
  currentHotelPrice,
  showLessBadge = false,
  hideSeeMore = false,
  onHotelClick,
  onSeeMoreClick,
  onDisplayed,
  onScrolled
}: BetterValueCarouselProps) => {
  useEffect(() => {
    if (hotels.length > 0) {
      onDisplayed?.(hotels)
    }
  }, [hotels.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fire scroll callback once per mount
  const hasScrolledRef = useRef(false)
  const handleScroll = useCallback(() => {
    if (hasScrolledRef.current) return
    hasScrolledRef.current = true
    onScrolled?.()
  }, [onScrolled])

  if (isLoading) {
    return <BetterValueCarouselSkeleton />
  }

  if (hotels.length === 0) {
    return null
  }

  return (
    <CarouselWrapper>
      <Text variant="titleM">
        <FormattedMessageWrapper
          id="betterValueStaysIn"
          defaultMessage="Better value stays in {city}"
          values={{city: locationName}}
        />
      </Text>

      <ScrollContainer onScroll={handleScroll}>
        {hotels.map((hotel, index) => (
          <SlideWrapper key={hotel.hotelId}>
            <BetterValueHotelCard
              hotel={hotel}
              currentHotelPrice={currentHotelPrice}
              showLessBadge={showLessBadge}
              onClick={() => onHotelClick?.(hotel, index + 1)}
            />
          </SlideWrapper>
        ))}
      </ScrollContainer>

      {!hideSeeMore && (
        <Box>
          <Button
            variant="tertiary"
            size="lg"
            fullWidth
            onClick={onSeeMoreClick}
          >
            <FormattedMessageWrapper id="seeMore" defaultMessage="See more" />
          </Button>
        </Box>
      )}
    </CarouselWrapper>
  )
}
